import { ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";

import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaSkeleton } from "@/shared/components/feedback/ColocaSkeleton";
import { ColocaDrawer } from "@/shared/components/overlays/ColocaDrawer";
import useTranslations from "@/shared/lang/useTranslations";
import { formatDateTime } from "@/shared/utils/dateTimeHelpers";

import type { OtcOrder } from "../../types/cryptoSwaps.types";
import { cryptoSwapOrderPath } from "../../utils/cryptoSwapLinks";
import { OrderDetailsList } from "../order/OrderDetailsList";
import { OrderAmounts } from "../shared/OrderAmounts";
import { CryptoSwapStatusPill } from "./CryptoSwapStatusPill";

export interface CryptoSwapOrderDrawerProps {
  order: OtcOrder | null;
  isLoading: boolean;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/** One order, read-only, over the list. The price lives here, next to a label saying which way it reads. */
export function CryptoSwapOrderDrawer({ order, isLoading, open, onOpenChange }: CryptoSwapOrderDrawerProps) {
  const { t, activeLanguage } = useTranslations("cryptoSwaps");

  return (
    <ColocaDrawer
      open={open}
      onOpenChange={onOpenChange}
      size="lg"
      title={order ? <OrderAmounts order={order} size="sm" /> : t("drawer_title")}
      description={
        order
          ? t("drawer_created_updated", {
              created: formatDateTime(order.created_at, { locale: activeLanguage }),
              updated: formatDateTime(order.updated_at, { locale: activeLanguage }),
            })
          : undefined
      }
      footer={
        order ? (
          <ColocaButton
            variant="primary"
            iconTrailing={ArrowRight}
            className="w-full"
            render={<Link to={cryptoSwapOrderPath(order.id)} />}
          >
            {t("drawer_open_order_page")}
          </ColocaButton>
        ) : undefined
      }
    >
      {isLoading || !order ? (
        <>
          <ColocaSkeleton className="h-24 rounded-2xl" />
          <ColocaSkeleton className="mt-3 h-40 rounded-2xl" />
        </>
      ) : (
        <>
          <section className="pb-4">
            <CryptoSwapStatusPill status={order.status} />
          </section>
          <section className="flex flex-col gap-3 border-t border-border pt-4">
            <h3 className="text-xs font-semibold tracking-[0.08em] text-muted-foreground uppercase">
              {t("summary_title")}
            </h3>
            <OrderDetailsList order={order} />
          </section>
        </>
      )}
    </ColocaDrawer>
  );
}
